/**
 * Wizard API Service
 *
 * Handles all API calls related to the onboarding wizard.
 */
import { apiClient } from './apiClient';

export interface WizardProgress {
  currentStep: string;
  completed: boolean;
  completedAt?: string;
  stepData?: {
    organizationId?: string;
    locationId?: string;
    preferenceId?: string;
    scenarioId?: string;
    subscriptionId?: string;
  };
}

export interface LocationStepData {
  organizationName: string;
  locationName: string;
  locationType: string;
  address?: string;
}

export interface MusicStepData {
  genres: string[];
  moods: string[];
}

export interface ScenarioStepData {
  name: string;
  description?: string;
}

export interface WizardPreview {
  organization: {
    name: string;
  };
  location: {
    name: string;
    type: string;
    address?: string;
  };
  musicPreferences: {
    genres: string[];
    moods: string[];
  };
  scenario: {
    name: string;
    description?: string;
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: string;
  trialDays: number;
  features: string[];
  recommended?: boolean;
}

/** Get current wizard progress */
export const getProgress = () =>
  apiClient.get<WizardProgress>('/api/wizard/progress');

/** Complete the location step (Step 1/5) */
export const completeLocationStep = (data: LocationStepData) =>
  apiClient.post<WizardProgress>('/api/wizard/steps/location', data);

/** Complete the music preferences step (Step 2/5) */
export const completeMusicStep = (data: MusicStepData) =>
  apiClient.post<WizardProgress>('/api/wizard/steps/music', data);

/** Complete the scenario naming step (Step 3/5) */
export const completeScenarioStep = (data: ScenarioStepData) =>
  apiClient.post<WizardProgress>('/api/wizard/steps/scenario', data);

/** Complete the preview step (Step 4/5) */
export const completePreviewStep = () =>
  apiClient.post<WizardProgress>('/api/wizard/steps/preview');

/**
 * Complete the subscription step (Step 5/5).
 * All plans start with a 14-day free trial during onboarding.
 */
export const completeSubscriptionStep = (plan: string) =>
  apiClient.post<WizardProgress>('/api/wizard/steps/subscription', { plan });

/** Get wizard preview data for Step 4/5 */
export const getPreviewData = () =>
  apiClient.get<WizardPreview>('/api/wizard/preview');

/** Get available location types (anonymous) */
export const getLocationTypes = () =>
  apiClient.get<string[]>('/api/wizard/location-types');

/** Get available music filters (genres and moods) (anonymous) */
export const getMusicFilters = () =>
  apiClient.get<{ genres: string[]; moods: string[] }>('/api/wizard/music-filters');

/** Get available subscription plans (anonymous) */
export const getSubscriptionPlans = () =>
  apiClient.get<{ plans: SubscriptionPlan[] }>('/api/subscription/plans');

export const wizardApi = {
  getProgress,
  completeLocationStep,
  completeMusicStep,
  completeScenarioStep,
  completePreviewStep,
  completeSubscriptionStep,
  getPreviewData,
  getLocationTypes,
  getMusicFilters,
  getSubscriptionPlans,
};

export default wizardApi;
